import {
  Form,
  FormTemplate,
  FormTemplateType,
  Patient,
  Project,
  Role,
  SubProject,
  User,
  UserSlim,
  Visit,
  VisitTemplate,
} from 'api/models';
import { Comment } from 'api/models/Comment';
import { FieldTemplate, FieldType } from 'api/models/FieldTemplate';
import { FormTemplateEditorMode } from 'routes/FormTemplateEditor/FormTemplateEditor';

export type GeneralIds = {
  id?: string;
  projectId?: string;
  patientId?: string;
  visitId?: string;
  formTemplateTypeId?: string;
  formId?: string;
  userId?: string;
};

export enum ModelTypes {
  user = 'users',
  project = 'projects',
  subProject = 'subProjects',
  role = 'roles',
  patient = 'patients',
  userSignUp = 'userSignUp',
  visit = 'visit',
  visitTemplate = 'visitTemplate',
  formTemplateType = 'formTemplateType',
  form = 'form',
  formTemplate = 'formTemplate',
  formTemplateForms = 'formTemplateForms',
  codeLibraries = 'codeLibraries',
  codeLibraryOption = 'codeLibraryOption',
  fieldTemplate = 'fieldTemplate',
  comment = 'comment',
}

export type Models =
  | User
  | Project
  | Role
  | Patient
  | Visit
  | VisitTemplate
  | FormTemplateType
  | Form
  | FormTemplate
  | UserSlim
  | FieldTemplate
  | SubProject
  | Comment;

export enum DialogTypes {
  addProject = 'addProject',
  addUser = 'addUser',
  addGroup = 'addGroup',
  confirmDelete = 'confirmDelete',
  addPatient = 'addPatient',
  addVisit = 'addVisit',
  addVisitTemplate = 'addVisitTemplate',
  addFormTemplateType = 'addFormTemplateType',
  addFieldDialog = 'addFieldDialog',
  formValidationErrors = 'formValidationErrors',
  editLayoutItem = 'editLayoutItem',
  editHeaderItem = 'editHeaderItem',
  formConfirmation = 'formConfirmation',
  editSelectableTableField = 'editSelectableTableField',
  comment = 'comment',
  reportTemplate = 'reportTemplate',
}

export enum SingularModelTypes {
  user = 'user',
  project = 'project',
  userSlim = 'userSlim',
  form = 'form',
  field = 'field',
  fieldTemplate = 'fieldTemplate',
}

type JSONModel = User | Project | SubProject | Role | UserSlim;

export type JSONApiResponse = {
  data: JSONApiResponseElement | JSONApiResponseElement[];
  included?: JSONApiResponseElement[];
};

export type JSONApiResponseElement = {
  type: SingularModelTypes;
  attributes: Omit<JSONModel, 'id'>;
  relationships?: {
    [key: string]: {
      data: {
        id: string;
        type: SingularModelTypes;
      }[];
    };
  };
  meta?: {
    totalCount?: number;
  };

  id: string;
};

export enum RoutePaths {
  root = '/',
  home = '/projects',
  login = '/login',
  passwordReset = '/password-reset',
  newPassword = '/users/:id/passwordReset',
  patients = '/projects/:projectId/patients',
  projectDashboard = '/projects/:projectId',
  projectEditor = '/projects/:projectId/edit',
  projectSelector = '/projects/selector',
  users = '/projects/:projectId/users',
  userEditor = '/projects/:projectId/users/:userId/edit',
  registerUser = '/projects/:projectId/users/:id/register',
  export = '/projects/:projectId/export',
  profile = '/projects/:projectId/profile',
  patientEditor = '/projects/:projectId/patients/:patientId/edit',
  patientViewer = '/projects/:projectId/patients/:patientId/view',
  patientCreator = '/projects/:projectId/patients/create',
  patientDetails = '/projects/:projectId/patients/:patientId',
  visitTemplate = '/projects/:projectId/visitTemplates/:visitTemplateId',
  formTemplateEditor = '/projects/:projectId/formTemplate/:formTemplateTypeId/form',
  formTemplateEditorView = '/projects/:projectId/formTemplate/:formTemplateTypeId/form/view',
  sampleTemplateEditor = '/projects/:projectId/formTemplate/:formTemplateTypeId/sample',
  sampleTemplateEditorView = '/projects/:projectId/formTemplate/:formTemplateTypeId/sample/view',
  forms = '/projects/:projectId/forms',
  form = '/projects/:projectId/patients/:patientId/visits/:visitId/formTemplateType/:formTemplateTypeId/forms',
  formEdit = '/projects/:projectId/patients/:patientId/visits/:visitId/formTemplateType/:formTemplateTypeId/forms/:formId',
  formView = '/projects/:projectId/patients/:patientId/visits/:visitId/formTemplateType/:formTemplateTypeId/forms/:formId/view',
  report = '/projects/:projectId/patients/:patientId/visits/:visitId/formTemplateType/:formTemplateTypeId/reports',
  reportEdit = '/projects/:projectId/patients/:patientId/visits/:visitId/formTemplateType/:formTemplateTypeId/reports/:formId',
  reportView = '/projects/:projectId/patients/:patientId/visits/:visitId/formTemplateType/:formTemplateTypeId/reports/:formId/view',
  import = '/projects/:projectId/import',
  reportTemplates = '/projects/:projectId/reports',
  reportTemplateEditor = '/projects/:projectId/reports/:formTemplateTypeId/edit',
}

// convert UserStatus to an enum
export enum UserStatus {
  pending = 'pending',
  active = 'active',
  inactive = 'inactive',
}

export enum UserPermissions {
  ReadProject = 'read_project',
  WriteProject = 'write_project',
  AdminProject = 'admin_project',
  ReadSubproject = 'read_subproject',
  WriteSubproject = 'write_subproject',
  AdminSubproject = 'admin_subproject',
  ReadProjectUser = 'read_project_user',
  WriteProjectUser = 'write_project_user',
  ReadSubprojectUser = 'read_subproject_user',
  WriteSubprojectUser = 'write_subproject_user',
  Superadmin = 'superadmin',
}

export enum RolePermissions {
  SuperAdmin = '999',
  ReadProject = '1',
  WriteProject = '2',
}

export enum SupportedIdentifiers {
  taj = 'taj',
  other = 'other',
  none = 'none',
}

export type Identifier = {
  id: string;
  type?: SupportedIdentifiers;
};

export enum OrderByDirection {
  asc = 'asc',
  desc = 'desc',
}

export type QueryParams = {
  pageSize?: number;
  pageNumber?: number;
  orderBy?: string;
  orderByDirection?: OrderByDirection;
  query?: string | null;
  isValidNow?: boolean;
  type?: FormTemplateEditorMode;
};

export type ResponseMeta = {
  totalCount?: number;
};

export enum VisitTemplateOrderConfig {
  first = 'first',
  not_first = 'not_first',
  last = 'last',
  any = 'any',
}

export interface OnAddProps {
  type: FieldType;
  path: string;
  columnIndex?: number;
}
